import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getShopDetails } from "./api";
import { useCart } from "./context/CartContext";
import AppDownloadNudge from "./components/AppDownloadNudge";
import ShopNavBar from "./components/ShopNavBar";
import EmptyState from "./components/EmptyState";

function ProductGridSkeleton() {
  return (
    <div className="px-4 mt-5 grid grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-100 overflow-hidden bg-white"
        >
          <div className="w-full aspect-square bg-gray-100 animate-pulse" />
          <div className="p-2.5 flex flex-col gap-2">
            <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
            <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ShopHome() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getShopDetails(shopId, { limit: 50 })
      .then((res) => {
        setShop(res.data.shop);
        setProducts(res.data.products || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [shopId]);

  return (
    <div className="min-h-screen pb-24">
      <ShopNavBar
        onBack={() => navigate("/shop/shops")}
        onCart={() => navigate(`/shop/${shopId}/cart`)}
        cartCount={itemCount}
      />

      {loading && (
        <>
          <div className="h-32 bg-gray-100 animate-pulse" />
          <div className="px-4 -mt-8 flex items-end gap-3">
            <div className="w-16 h-16 rounded-xl bg-gray-200 border-4 border-white shrink-0 animate-pulse" />
            <div className="flex-1 flex flex-col gap-2 pb-1">
              <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
          <ProductGridSkeleton />
        </>
      )}

      {!loading && (error || !shop) && (
        <EmptyState
          title="Couldn't load this shop"
          subtitle={error || "This shop may no longer be available."}
        />
      )}

      {!loading && shop && (
        <>
          <div
            className="h-32 bg-gray-100 bg-cover bg-center"
            style={
              shop.bannerImageUrl
                ? { backgroundImage: `url(${shop.bannerImageUrl})` }
                : undefined
            }
          />
          <div className="px-4 -mt-8 flex items-end gap-3">
            <div className="w-16 h-16 rounded-xl bg-white border-4 border-white shadow-md overflow-hidden flex items-center justify-center shrink-0">
              {shop.logoUrl ? (
                <img
                  src={shop.logoUrl}
                  alt={shop.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="otaFont font-bold text-xl">
                  {shop.name?.[0]}
                </span>
              )}
            </div>
            <div className="min-w-0 pb-1">
              <h1 className="font-bold text-lg otaDark truncate">
                {shop.name}
              </h1>
              {shop.description && (
                <p className="text-xs otaLight line-clamp-1">
                  {shop.description}
                </p>
              )}
            </div>
          </div>

          <div className="px-4 mt-5">
            <AppDownloadNudge compact />
          </div>

          {products.length === 0 ? (
            <EmptyState
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 7h16l-1.5 11.2a2 2 0 01-2 1.8H7.5a2 2 0 01-2-1.8L4 7z"
                    stroke="#9CA3AF"
                    strokeWidth="1.6"
                  />
                  <path d="M8 7V5a4 4 0 018 0v2" stroke="#9CA3AF" strokeWidth="1.6" />
                </svg>
              }
              title="No products yet"
              subtitle="This shop hasn't listed anything for sale yet — check back soon."
            />
          ) : (
            <div className="px-4 mt-5 grid grid-cols-2 gap-3">
              {products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/shop/${shopId}/product/${p.id}`)}
                  className="text-left rounded-xl border border-gray-100 cardShadow overflow-hidden bg-white active:scale-[0.98] transition-transform"
                >
                  <div className="w-full aspect-square bg-gray-100">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-medium otaDark line-clamp-2 leading-snug min-h-[2.5em]">
                      {p.title}
                    </p>
                    <p className="text-sm font-bold otaFont mt-1">
                      ₹{p.price.toFixed(0)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {itemCount > 0 && (
        <button
          onClick={() => navigate(`/shop/${shopId}/cart`)}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[448px] otaGradientButton text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 z-30 shadow-lg active:scale-[0.98] transition-transform"
        >
          View Cart ({itemCount})
        </button>
      )}
    </div>
  );
}
